import type { LayerSpecification } from 'mapbox-gl';
import { CONSUMPTION_CLASSES, closeRing } from './domain';
import { PC } from './tokens';
import type { LngLat, PlanBuilding } from './types';

type FC = GeoJSON.FeatureCollection;
export const EMPTY: FC = { type: 'FeatureCollection', features: [] };

export function buildingsFC(buildings: PlanBuilding[], props: (b: PlanBuilding) => Record<string, number | string>): FC {
  return {
    type: 'FeatureCollection',
    features: buildings.map((b) => ({ type: 'Feature', id: b.num, properties: { id: b.num, fid: b.id, ...props(b) }, geometry: b.geometry })),
  };
}

export function ringFC(rings: LngLat[][]): FC {
  return {
    type: 'FeatureCollection',
    features: rings.filter((r) => r.length >= 3).map((r, i) => ({
      type: 'Feature', id: i + 1, properties: { id: i + 1 }, geometry: { type: 'Polygon', coordinates: [closeRing(r)] },
    })),
  };
}

/** Polygone en cours de dessin : sommets + ligne (+ surface quand ≥ 3 points). */
export function drawFC(points: LngLat[], closed: boolean): FC {
  const features: GeoJSON.Feature[] = points.map((p, i) => ({ type: 'Feature', id: i + 1, properties: { id: i + 1, first: i === 0 ? 1 : 0 }, geometry: { type: 'Point', coordinates: p } }));
  if (points.length >= 2) {
    features.push({ type: 'Feature', id: 1000, properties: { id: 1000 }, geometry: { type: 'LineString', coordinates: closed ? closeRing(points) : points } });
  }
  if (points.length >= 3) {
    features.push({ type: 'Feature', id: 1001, properties: { id: 1001 }, geometry: { type: 'Polygon', coordinates: [closeRing(points)] } });
  }
  return { type: 'FeatureCollection', features };
}

/** Carte de l'assistant : bâtiments UM inclus / exclus, contour, outil de dessin. */
export function perimeterLayers(): LayerSpecification[] {
  return [
    { id: 'pc-wiz-building', type: 'fill', source: 'buildings', paint: { 'fill-color': ['case', ['==', ['get', 'inc'], 1], PC.included, PC.excluded] as never, 'fill-opacity': ['case', ['==', ['get', 'inc'], 1], 0.75, 0.45] as never } },
    { id: 'pc-wiz-outline', type: 'line', source: 'outline', paint: { 'line-color': PC.outline, 'line-width': 2, 'line-dasharray': [2, 1.5] } },
    { id: 'pc-draw-fill', type: 'fill', source: 'draw', filter: ['==', ['geometry-type'], 'Polygon'], paint: { 'fill-color': PC.draw, 'fill-opacity': 0.08 } },
    { id: 'pc-draw-line', type: 'line', source: 'draw', filter: ['==', ['geometry-type'], 'LineString'], paint: { 'line-color': PC.draw, 'line-width': 2 } },
    { id: 'pc-draw-rubber', type: 'line', source: 'rubber', paint: { 'line-color': PC.draw, 'line-width': 1.5, 'line-dasharray': [2, 2] } },
    {
      id: 'pc-draw-vertex', type: 'circle', source: 'draw', filter: ['==', ['geometry-type'], 'Point'],
      paint: { 'circle-radius': ['case', ['==', ['get', 'first'], 1], 6, 4] as never, 'circle-color': '#ffffff', 'circle-stroke-color': PC.draw, 'circle-stroke-width': 2 },
    },
  ];
}

const consoColor = (() => {
  const expr: unknown[] = ['step', ['get', 'conso'], CONSUMPTION_CLASSES[0].color];
  CONSUMPTION_CLASSES.slice(0, -1).forEach((c, i) => expr.push(c.max, CONSUMPTION_CLASSES[i + 1].color));
  return expr;
})();

/** Carte du projet : bâtiments colorés par conso corrigée, contour du périmètre, bâtiment sélectionné. */
export function projectLayers(pickedNum: number | null): LayerSpecification[] {
  return [
    { id: 'pc-prj-building', type: 'fill', source: 'buildings', paint: { 'fill-color': consoColor as never, 'fill-opacity': 0.9 } },
    { id: 'pc-prj-building-line', type: 'line', source: 'buildings', paint: { 'line-color': '#ffffff', 'line-width': 0.4, 'line-opacity': 0.6 } },
    { id: 'pc-prj-outline', type: 'line', source: 'outline', paint: { 'line-color': PC.outline, 'line-width': 2.5, 'line-dasharray': [2, 1.5] } },
    { id: 'pc-prj-picked', type: 'line', source: 'buildings', filter: ['==', ['get', 'id'], pickedNum ?? -1], paint: { 'line-color': '#6ED2B1', 'line-width': 3.5 } },
  ];
}
